import {useState, useEffect} from 'react';
import {useHistory, useLocation} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {
  lockTenantUser,
  lockUser,
  unlockTenantUser,
  unlockUser
} from '../../api/users';
import ConfirmationPopup from '../common/popup/ConfirmationPopup';
import {Button, ButtonWithLoader} from '../common/Button';
import {setTitle} from '../../store/header/header.slice';
import {useAppDispatch} from '../../store/hooks';
import ActionButtons from '../common/layout/ActionButtons';
import {EditUserEntityProps} from '../../constants/types';
import StaticField from '../common/StaticField';
import routes from './routes';
import EnsurePermission from '../common/EnsurePermission';
import permissionsInfo from '../../constants/permissionsInfo';
import {useSnackbarHook} from '../../hooks/useSnackbar';

type AccountProps = {
  id: string,
  firstName: string,
  lastName: string,
  email: string,
  locked: boolean,
  entity: EditUserEntityProps
};

const UserEditAccount = (
    {id, firstName, lastName, email, locked, entity}: AccountProps
) => {
  const {t} = useTranslation(['user', 'common']);
  const {pathname} = useLocation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const {addSuccessToast, addErrorToast} = useSnackbarHook();

  useEffect(() => {
    dispatch(setTitle(''));
  }, []);

  const [isLocked, setLocked] = useState(!!locked);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLocking, setIsLocking] = useState(false);
  const [isPopupShown, setIsPopupShown] = useState(false);
  const isSysConfPath = pathname.includes(routes.users());

  const popupLabels = entity.popupLabels(firstName, lastName);

  const handleLockUser = async () => {
    setIsLocking(true);

    try {
      if (isLocked) {
        isSysConfPath ? await unlockUser(id) : await unlockTenantUser(id);
        setLocked(false);
        addSuccessToast(t('toasts.unlockUserMessage'));
      } else {
        isSysConfPath ? await lockUser(id) : await lockTenantUser(id);
        setLocked(true);
        addSuccessToast(t('toasts.lockUserMessage'));
      }
    } catch (error) {
      addErrorToast(error?.message);
    } finally {
      setIsLocking(false);
    }
  };

  const backToList = () => {
    history.push(entity.returnRoute);
  };

  const handleDeleteUser = async () => {
    setIsDeleting(true);

    try {
      await entity.deleteUser(id);
      backToList();
      addSuccessToast(t('toasts.deleteUserMessage'));
    } catch (error) {
      addErrorToast(error?.message);
    } finally {
      setIsPopupShown(false);
      setIsDeleting(false);
    }
  };

  return (
    <div className="tab-content">
      <StaticField label={t('form.fields.firstName.label')} value={firstName} />
      <StaticField label={t('form.fields.lastName.label')} value={lastName} />
      <StaticField label={t('form.fields.email.label')} value={email} />

      <ActionButtons className="space-between">
        <Button
          label={t('common:backToList')}
          variant="outlined"
          color="secondary"
          onClick={backToList}
          disabled={isLocking || isDeleting}
        />

        <div className="flex-end">
          <EnsurePermission elementPermissions={
            isSysConfPath ?
              permissionsInfo.users.lock.all :
              permissionsInfo.users.lock.tenant
          }>
            <ButtonWithLoader
              disabled={isDeleting}
              isSubmitting={isLocking}
              testId="edit-tenant"
              label={isLocked ? t('unlockUser') : t('lockUser')}
              variant="outlined"
              color="secondary"
              onClick={handleLockUser}
            />
          </EnsurePermission>

          <ButtonWithLoader
            onClick={() => setIsPopupShown(true)}
            testId="freeze-tenant"
            label={t('deleteAccount')}
            variant="text"
            color="secondary"
            disabled={isLocking}
            isSubmitting={isDeleting}
          />
        </div>
      </ActionButtons>

      <ConfirmationPopup
        labels={popupLabels}
        isOpen={isPopupShown}
        setIsOpen={setIsPopupShown}
        confirmableAction={handleDeleteUser}
        isActionProcessing={isDeleting}
      />
    </div>
  );
};

export default UserEditAccount;
